import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col, Button } from './';
import { css } from 'styled-components';

import ElasticAutocomplete from './search/autosuggest';
import Postcode from './search/location';
import SearchIcon from './search/icons/search';
import LocationIcon from './search/icons/location';

export const sharedInputCss = css`
  line-height: 24px;
  font-size: 20px;
  padding: 17px 0;
  width: 100%;
  margin: 0;
  color: #2f3034;
  border-radius: 4px !important;
  padding-left: 40px;
  ::-webkit-input-placeholder {
    font-size: 16px;
    @media (max-width: ${props => props.theme.breakpoints.sm}rem) {
      font-size: 13px;
    }
  }
`;

const Category = ElasticAutocomplete.extend`
  input {
    @media (min-width: ${props => props.theme.breakpoints.md}rem) {
      border-radius: 4px 0 0 4px !important;
    }
  }
`;

const GoButton = Button.extend`
  padding: 21px 0;
  line-height: 1.15;
  margin: 0;
  @media (min-width: ${props => props.theme.breakpoints.md}rem) {
    border-radius: 0 4px 4px 0;
  }
`;

const Column = Col.extend`
  position: relative;

  .search-icon {
    position: absolute;
    left: 12px;
    top: 22px;
    z-index: 1;
  }
`;

const SearchContainer = Container.extend`
  position: relative;
  background: ${props => (props.showBorder ? 'rgba(0, 0, 0, 0.4)' : 'none')};
  padding: ${props => (props.showBorder ? '10px' : '0')};

  @media (min-width: ${props => props.theme.breakpoints.sm}rem) {
    border-radius: 4px;
    padding: ${props => (props.showBorder ? '20px' : '0')};
  }
`;

export const LocationType = PropTypes.shape({
  suburb: PropTypes.string,
  postcode: PropTypes.string,
  suburb_seo_key: PropTypes.string
});

export const SuggestionType = PropTypes.oneOfType([
  PropTypes.shape({
    practice_seo_key: PropTypes.string.isRequired,
    prefix_text: PropTypes.oneOf(['Browse ', 'Let us find you ']).isRequired,
    practice_person_plural: PropTypes.string.isRequired
  }),
  PropTypes.shape({
    directory_url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  })
]);

export class SearchBar extends Component {
  static propTypes = {
    onSuggestionSelected: PropTypes.func,
    onSubmit: PropTypes.func,
    onFocusAutosuggest: PropTypes.func,
    onBlurAutosuggest: PropTypes.func,
    onFocusLocation: PropTypes.func,
    onBlurLocation: PropTypes.func,
    onButtonClick: PropTypes.func,
    initialLocation: LocationType,
    initialSuggestion: SuggestionType,
    onLocationDidChange: PropTypes.func,
    showPopularCategories: PropTypes.bool,
    categoryPlaceholder: PropTypes.string,
    postcodePlaceholder: PropTypes.string,
    buttonText: PropTypes.string,
    className: PropTypes.string,
    showBorder: PropTypes.bool
  };

  static defaultProps = {
    onSubmit: url => (window.location = url),
    showBorder: true
  };

  state = {
    postcodeValue: '',
    autosuggestion: null,
    locationSelection: null,
    highlightedPostcodeSuggestion: null,
    suggestionValue: ''
  };

  constructor(props) {
    super(props);
    const { initialLocation, initialSuggestion } = this.props;
    if (initialLocation) {
      this.state.postcodeValue = `${initialLocation.suburb} - ${initialLocation.postcode}`;
      this.state.locationSelection = initialLocation;
    }
    if (initialSuggestion) {
      this.state.autosuggestion = initialSuggestion;
      if (initialSuggestion.prefix_text) {
        this.state.suggestionValue = initialSuggestion.practice_person_plural || '';
      } else {
        this.state.suggestionValue = initialSuggestion.name;
      }
    }
  }

  onChangePostcode = postcodeValue => {
    this.setState({ postcodeValue });
    if (!postcodeValue) this.props.onLocationDidChange();
  };

  onChangeAutosuggest = (_, { newValue }) => {
    this.setState({ suggestionValue: newValue });
  };

  onPostcodeSelected = (event, { suggestion, suggestionValue }) => {
    this.setState({ location: suggestionValue, locationSelection: suggestion });
    this.props.onLocationDidChange && this.props.onLocationDidChange(suggestion);
  };

  onSuggestionSelected = (event, { suggestion, suggestionValue }) => {
    this.setState({ autosuggestion: suggestion });

    //if we have a category and postcode/location OR it is "post a job", navigate to url now
    if (
      suggestion.prefix_text === 'Let us find you ' ||
      this.state.postcodeValue ||
      this.state.locationSelection
    ) {
      setTimeout(() => this.navigateToSuggestion(), 100);
    } else {
      // move focus to postcode input
      setTimeout(() => document.querySelector('#postcode-search').focus(), 100);
    }
    this.props.onSuggestionSelected && this.props.onSuggestionSelected(suggestion, suggestionValue);
  };

  onSearch = url => {
    const { postcodeValue, suggestionValue } = this.state;
    this.props.onSubmit(url.toLowerCase(), { postcodeValue, suggestionValue });
  };

  navigateToSuggestion = () => {
    const {
      postcodeValue,
      autosuggestion,
      highlightedSuggestion,
      highlightedPostcodeSuggestion,
      locationSelection,
      suggestionValue
    } = this.state;

    const suggestion = autosuggestion || highlightedSuggestion;
    if (!suggestionValue) {
      alert('Please enter a keyword or select a category to search');
    }

    if (suggestion && suggestion.prefix_text === 'Let us find you ') {
      const locQuery = postcodeValue ? `?w=${postcodeValue}` : '';
      this.onSearch(`/find/${suggestion.practice_seo_key}/get_quotes_simple${locQuery}`);
    } else if (suggestion && suggestion.prefix_text === 'Browse ') {
      const locationSelected = locationSelection || highlightedPostcodeSuggestion;
      if (!locationSelected) {
        if (postcodeValue) {
          //console.log('no selected location but typed a postcode or suburb');
          if (postcodeValue.match(/^\d{4}$/)) {
            // e.g: "2077" -> "/search/plumbers/postcode/2077"
            this.onSearch(`/search/${suggestion.practice_seo_key}/postcode/${postcodeValue}`);
          } else {
            // e.g: "DeE  Why" -> "/suburb_help/dee_why?cat=plumbers"
            //
            // But, very unlikely users will land here
            // The default highlight selection (if any) should be applied above
            this.onSearch(
              `/suburb_help/${postcodeValue.replace(/\s+/g, '_').toLowerCase()}?cat=${
                suggestion.practice_seo_key
              }`
            );
          }
        } else {
          //console.log('no selected location or typed postcode');
          this.onSearch(`/find/${suggestion.practice_seo_key}`);
        }
      } else {
        this.onSearch(
          `/find/${suggestion.practice_seo_key}/${locationSelected.state_key}/${
            locationSelected.suburb_seo_key
          }`
        );
      }
    } else if (suggestion && suggestion.directory_url.includes('/connect/')) {
      this.onSearch(suggestion.directory_url);
    } else if (suggestionValue) {
      const locQuery = postcodeValue ? `&w=${postcodeValue}` : '';
      this.onSearch(`/get_quotes_simple?search_str=${suggestionValue}${locQuery}`);
    }
  };

  onSubmit = e => {
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
    this.navigateToSuggestion();
  };

  onBlurPostCode = (_, { highlightedSuggestion }) => {
    this.setState({ highlightedPostcodeSuggestion: highlightedSuggestion });
    this.props.onBlurLocation();
  };

  onBlur = (event, { highlightedSuggestion }) => {
    this.props.onBlurAutosuggest && this.props.onBlurAutosuggest(event, highlightedSuggestion);
    this.setState({ highlightedSuggestion });
  };

  render() {
    const { postcodeValue, suggestionValue, highlightedSuggestion, autosuggestion } = this.state;
    const {
      onFocusAutosuggest,
      onFocusLocation,
      showPopularCategories,
      categoryPlaceholder,
      postcodePlaceholder,
      buttonText,
      className,
      showBorder
    } = this.props;
    const suggestion = autosuggestion || highlightedSuggestion;
    const isDirectory =
      suggestion && suggestion.prefix_text && suggestion.prefix_text.trim() === 'Browse';

    return (
      <form
        action={
          isDirectory
            ? 'https://www.homeimprovementpages.com.au/index.php?m=search&a=search_results'
            : 'https://www.homeimprovementpages.com.au/get_quotes_simple'
        }
        onSubmit={this.onSubmit}
        ref={form => (this.form = form)}
        className={className}
      >
        <SearchContainer showBorder={showBorder}>
          <Row gutter={0}>
            <Column xs={12} md={7} gutter={0} mb={{ xs: 10 / 16, md: 0 }}>
              <LocationIcon />
              <Category
                tabIndex={1}
                id="category-search"
                onSuggestionSelected={this.onSuggestionSelected}
                onChange={this.onChangeAutosuggest}
                onFocus={onFocusAutosuggest}
                onBlur={this.onBlur}
                value={suggestionValue}
                placeholder={categoryPlaceholder || 'Search by trade or business name'}
                showPopularCategories={showPopularCategories}
                data-label={'single-search-category'}
              />
              <input
                type="hidden"
                name="search_str"
                value={
                  (highlightedSuggestion && highlightedSuggestion.practice_seo_key) ||
                  suggestionValue
                }
              />
            </Column>
            <Column xs={12} md={4} gutter={0} mb={{ xs: 10 / 16, md: 0 }}>
              <SearchIcon />
              <Postcode
                tabIndex={2}
                id="postcode-search"
                name={isDirectory ? 'search_zip' : 'w'}
                placeholder={postcodePlaceholder || 'Australia'}
                value={postcodeValue}
                onSuggestionSelected={this.onPostcodeSelected}
                onChange={this.onChangePostcode}
                onFocus={onFocusLocation}
                highlightFirstSuggestion={!/^\d+$/.test(postcodeValue)}
                onBlur={this.onBlurPostCode}
                data-label={'single-search-location'}
              />
            </Column>
            <Column xs={12} md={1} gutter={0}>
              {isDirectory && (
                <span>
                  <input type="hidden" name="m" value="search" />
                  <input type="hidden" name="a" value="search_results" />
                  <input type="hidden" name="dosql" value="do_search" />
                  <input type="hidden" name="search_code" value="p" />
                  <input type="hidden" name="search_what_header" value="what" />
                  <input type="hidden" name="search_where_header" value="where" />
                  <input type="hidden" name="loctype" value="zip" />
                </span>
              )}
              <GoButton
                primary
                block
                type="submit"
                tabIndex={3}
                data-label={'single-search-submit'}
                /* @joe do we need this?
                it means that navigateToSuggestion is getting called twice */
                /* onClick={this.navigateToSuggestion} */
              >
                {buttonText || 'Go'}
              </GoButton>
            </Column>
          </Row>
        </SearchContainer>
      </form>
    );
  }
}
